
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { adminPrivilegedService, AdminOverviewStats } from '@/services/admin/adminPrivilegedService';
import { Users, Home, BookOpen, Calendar, ShoppingCart, Activity, UserCheck } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

const AdminOverview: React.FC = () => {
  const [stats, setStats] = useState<AdminOverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminPrivilegedService.getOverviewStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load overview stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 7 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6 mt-2">Overview</h2>
        <Card className="border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 font-medium">Error loading statistics</p>
                <p className="text-sm text-red-500 mt-1">{error}</p>
              </div>
              <Badge variant="destructive">Error</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!stats) return null;

  const overviewCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'text-blue-600',
      description: 'All registered users'
    },
    {
      title: 'Multi-Member Families',
      value: stats.totalFamilies,
      icon: Home,
      color: 'text-green-600',
      description: 'Families with >1 member'
    },
    {
      title: 'Single-User Accounts',
      value: stats.singleUserAccounts,
      icon: UserCheck,
      color: 'text-gray-600',
      description: 'Individual users without family'
    },
    {
      title: 'Total Recipes',
      value: stats.totalRecipes,
      icon: BookOpen,
      color: 'text-orange-600',
      description: 'All recipes in database'
    },
    {
      title: 'Meal Plans',
      value: stats.totalMealPlans,
      icon: Calendar,
      color: 'text-purple-600',
      description: 'Planned meals across all families'
    },
    {
      title: 'Shopping Lists',
      value: stats.totalShoppingLists,
      icon: ShoppingCart,
      color: 'text-pink-600',
      description: 'Generated shopping lists'
    },
    {
      title: 'Activity Logs',
      value: stats.totalActivityLogs,
      icon: Activity,
      color: 'text-indigo-600',
      description: 'Total family activity entries'
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold mt-2">Overview</h2>
        <Badge variant="default" className="bg-green-100 text-green-800">
          Live Data
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {overviewCards.map((card, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {card.title}
              </CardTitle>
              <card.icon className={`h-4 w-4 ${card.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {card.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminOverview;
